import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { apiService } from '../services/api';
import { Customer, CustomerCreate, Contract, ContractCreate, PricingHeader } from '../types';
import Modal from '../components/Modal';
import CustomerForm from '../components/CustomerForm';
import ContractForm from '../components/ContractForm';
import ContractCreation from '../components/ContractCreation';
import FilterToggle from '../components/FilterToggle';
import BulkOperations from '../components/BulkOperations';
import SortableTable, { SortableColumn } from '../components/SortableTable';
import SearchInput from '../components/SearchInput';
// import { useCustomers } from '../hooks/useCustomers';

const Customers: React.FC = () => {
  const navigate = useNavigate();
  // Server-side data state
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [customersLoading, setCustomersLoading] = useState<boolean>(false);
    const [showModal, setShowModal] = useState(false);
  const [showContractModal, setShowContractModal] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState<Customer | null>(null);
  const [contractCustomer, setContractCustomer] = useState<Customer | null>(null);
  const [pricingHeaders, setPricingHeaders] = useState<PricingHeader[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [filterStatus, setFilterStatus] = useState<'all' | 'active' | 'deleted'>('active');
  const [filterType, setFilterType] = useState<'all' | 'supervisor' | 'controller' | 'unsupervised'>('unsupervised');
  // Bulk operations state
  const [selectedCustomers, setSelectedCustomers] = useState<number[]>([]);
  const [showBulkOps, setShowBulkOps] = useState(false);
  const [creatingControllerFor] = useState<Customer | null>(null);
  const [showContractCreation, setShowContractCreation] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [pageSize, setPageSize] = useState(10);

  // Stable search handler to avoid resetting page on every render via SearchInput debounce
  const handleSearch = useCallback((term: string) => {
    setSearchTerm(term);
    setCurrentPage(1);
  }, []);

  // Server-side fetch based on filters/pagination
  const fetchCustomers = useCallback(async () => {
    try {
      setCustomersLoading(true);
      // Map filters to API params
      const customerTypeParam = filterType === 'supervisor' || filterType === 'controller' ? filterType : undefined;
      const isSupervisedParam = filterType === 'unsupervised' ? false : undefined;
      const isActiveParam = filterStatus === 'active' ? true : filterStatus === 'deleted' ? false : undefined;
      const searchParam = searchTerm.trim() || undefined;

      const response = await apiService.getCustomers(
        currentPage,
        pageSize,
        customerTypeParam,
        undefined, // paymentStatus
        isSupervisedParam,
        searchParam,
        isActiveParam
      );

      const items = Array.isArray((response as any).items) ? (response as any).items : Array.isArray(response) ? (response as any) : [];
      const total = (response as any).total ?? items.length;

      setCustomers(items);
            setTotalPages(Math.max(1, Math.ceil(total / pageSize)));
    } catch (error) {
      console.error('Error fetching customers:', error);
      setCustomers([]);
            setTotalPages(1);
    } finally {
      setCustomersLoading(false);
    }
  }, [currentPage, pageSize, filterType, filterStatus, searchTerm]);

  useEffect(() => {
    fetchCustomers();
  }, [fetchCustomers]);

  // Define table columns
  const tableColumns: SortableColumn[] = [
    {
      key: 'customer_name',
      label: 'Name',
      render: (value, customer) => (
        <Link 
          to={`/customers/${customer.id}`} 
          style={{ textDecoration: 'none', color: '#3498db', fontWeight: '500' }}
        >
          {value || 'N/A'}
        </Link>
      )
    },
    {
      key: 'customer_type',
      label: 'Type',
      render: (value) => (
        <span className={`badge ${
          value === 'supervisor' ? 'badge-info' : 
          value === 'controller' ? 'badge-success' : 
          'badge-secondary'
        }`}>
          {value || 'Regular'}
        </span>
      )
    },
    {
      key: 'server_name',
      label: 'Server',
      render: (value) => value || 'N/A'
    },
    {
      key: 'customer_email_address',
      label: 'Email',
      render: (value) => value || 'N/A'
    },
    {
      key: 'customer_telephone_number',
      label: 'Phone',
      render: (value) => value || 'N/A'
    },
    {
      key: 'is_active',
      label: 'Status',
      render: (value) => (
        <span className={`badge ${value ? 'badge-success' : 'badge-danger'}`}>
          {value ? 'Active' : 'Inactive'}
        </span>
      )
    },
    {
      key: 'actions',
      label: 'Actions',
      sortable: false,
      render: (_, customer) => (
        <div className="action-buttons">
          <button
            className="btn btn-secondary btn-sm"
            onClick={() => handleEdit(customer)}
            style={{ marginRight: '5px' }}
          >
            Edit
          </button>
          <button
            className="btn btn-primary btn-sm"
            onClick={() => handleCreateContract(customer)}
            style={{ marginRight: '5px' }}
          >
            Create Contract
          </button>
          {customer.is_active ? (
            <button
              className="btn btn-danger btn-sm"
              onClick={() => handleDelete(customer.id)}
            >
              Delete
            </button>
          ) : (
            <button
              className="btn btn-success btn-sm"
              onClick={() => handleRestore(customer.id)}
            >
              Restore
            </button>
          )}
        </div>
      )
    }
  ];

  const handleRowSelect = (selectedRows: Customer[]) => {
    setSelectedCustomers(selectedRows.map(customer => customer.id));
  };

  useEffect(() => {
    fetchPricingHeaders();
  }, []);

  // Recalculate total pages when filtered or page size changes
  // Recompute total pages handled in fetch via total count

  const fetchPricingHeaders = async () => {
    try {
      const response = await apiService.getPricingHeaders(0, 100);
      setPricingHeaders(Array.isArray(response) ? response : []);
    } catch (error) {
      console.error('Error fetching pricing headers:', error);
      setPricingHeaders([]);
    }
  };

  const handleCreate = () => {
    setEditingCustomer(null);
    setShowModal(true);
  };

  const handleEdit = (customer: Customer) => {
    setEditingCustomer(customer);
    setShowModal(true);
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('Are you sure you want to delete this customer?')) {
      try {
        await apiService.deleteCustomer(id);
        fetchCustomers();
      } catch (error) {
        console.error('Error deleting customer:', error);
      }
    }
  };

  const handleRestore = async (id: number) => {
    try {
      await apiService.updateCustomer(id, { is_active: true });
      fetchCustomers();
    } catch (error) {
      console.error('Error restoring customer:', error);
    }
  };

  const handleSubmit = async (data: CustomerCreate) => {
    try {
      if (editingCustomer) {
        await apiService.updateCustomer(editingCustomer.id, data);
      } else {
        await apiService.createCustomer(data);
      }
      setShowModal(false);
      fetchCustomers();
    } catch (error) {
      console.error('Error saving customer:', error);
    }
  };

  const handleCreateContract = (customer: Customer) => {
    if (customer.customer_type === 'supervisor' || customer.customer_type === 'controller') {
      setContractCustomer(customer);
      setShowContractCreation(true);
    } else {
      setContractCustomer(customer);
      setShowContractModal(true);
    }
  };

  const handleContractSubmit = async (data: ContractCreate) => {
    try {
      const result = await apiService.createContract(data);
      setShowContractModal(false);
      
      // Extract the contract ID from the result
      let contractId: string;
      if ('contract' in result && 'contract_lines' in result) {
        // Supervisor contract response
        contractId = result.contract.contract_id;
        console.log('Supervisor contract created:', {
          contractId: contractId,
          linesCreated: result.contract_lines.length,
          errors: result.errors
        });
      } else {
        // Regular contract response
        contractId = (result as Contract).contract_id;
      }
      
      // Navigate to contract details page
      navigate(`/contracts/${contractId}`);
    } catch (error) {
      console.error('Error creating contract:', error);
    }
  };

  
  
  
  if (customersLoading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
      </div>
    );
  }

  if (creatingControllerFor) {
    return (
      <div className="loading">
        <div className="spinner"></div>
        <p style={{ marginTop: '20px' }}>Creating controller for {creatingControllerFor.customer_name}...</p>
      </div>
    );
  }

  return (
    <div>
      <div className="page-header">
        <h1>Customers</h1>
        <button className="btn btn-primary" onClick={handleCreate}>
          + Create Customer
        </button>
      </div>

      {/* Action Bar */}
      <div className="bg-white p-4 rounded-lg shadow-md mb-4 flex gap-2">
        <button
          onClick={() => setShowBulkOps(!showBulkOps)}
          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
          disabled={selectedCustomers.length === 0}
        >
          Bulk Operations ({selectedCustomers.length})
        </button>
        <button
          onClick={() => setSelectedCustomers([])}
          className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700"
        >
          Clear Selection
        </button>
      </div>

      {/* Bulk Operations Panel */}
      {showBulkOps && (
        <div className="mb-4">
          <BulkOperations
            entityType="customers"
            selectedIds={selectedCustomers}
            onOperationComplete={() => {
              setSelectedCustomers([]);
              fetchCustomers();
            }}
            onClearSelection={() => setSelectedCustomers([])}
          />
        </div>
      )}

      <div className="card">
        <div className="card-header">
          <div style={{ display: 'flex', gap: '20px', alignItems: 'center', flexWrap: 'wrap' }}>
            <SearchInput
              onSearch={handleSearch}
              placeholder="Search customers..."
              className="flex-1"
              initialValue={searchTerm}
            />
            <div>
              <label style={{ marginRight: '10px', fontWeight: 'bold' }}>Type:</label>
              <FilterToggle
                value={filterType}
                onChange={(val) => { setFilterType(val as any); setCurrentPage(1); }}
                options={[
                  { value: 'all', label: 'All' },
                  { value: 'supervisor', label: 'Supervisors' },
                  { value: 'controller', label: 'Controllers' },
                  { value: 'unsupervised', label: 'Unsupervised' }
                ]}
              />
            </div>
            <div>
              <label style={{ marginRight: '10px', fontWeight: 'bold' }}>Status:</label>
              <FilterToggle
                value={filterStatus}
                onChange={(val) => { setFilterStatus(val as any); setCurrentPage(1); }}
                options={[
                  { value: 'all', label: 'All' },
                  { value: 'active', label: 'Active' },
                  { value: 'deleted', label: 'Deleted' }
                ]}
              />
            </div>
            <div>
              <label style={{ marginRight: '10px', fontWeight: 'bold' }}>Page Size:</label>
              <select
                value={pageSize}
                onChange={(e) => {
                  setPageSize(Number(e.target.value));
                  setCurrentPage(1); // Reset to first page when changing page size
                }}
                style={{
                  padding: '4px 8px',
                  border: '1px solid #ccc',
                  borderRadius: '4px',
                  fontSize: '14px'
                }}
              >
                <option value={5}>5</option>
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
            </div>
          </div>
        </div>

        <SortableTable
          data={customers}
          columns={tableColumns}
          onRowSelect={handleRowSelect}
          selectedRows={customers.filter(customer => selectedCustomers.includes(customer.id))}
          rowKey="id"
          loading={customersLoading}
          emptyMessage="No customers found. Try adjusting your search or filters."
        />

        {/* Pagination */}
        <div className="pagination">
          <button
            onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
            disabled={currentPage <= 1}
          >
            Previous
          </button>
          <span>Page {Math.max(1, currentPage)} of {Math.max(1, totalPages)}</span>
          <button
            onClick={() => setCurrentPage(prev => Math.min(prev + 1, Math.max(1, totalPages)))}
            disabled={currentPage >= totalPages}
          >
            Next
          </button>
        </div>
      </div>

      {showModal && (
        <Modal
          title={editingCustomer ? 'Edit Customer' : 'Create Customer'}
          onClose={() => setShowModal(false)}
        >
          <CustomerForm
            initialData={editingCustomer}
            onSubmit={handleSubmit}
            onCancel={() => setShowModal(false)}
          />
        </Modal>
      )}

      {showContractModal && contractCustomer && (
        <Modal
          title={`Create Contract for ${contractCustomer.customer_name}`}
          onClose={() => setShowContractModal(false)}
        >
          <ContractForm
            initialData={null}
            pricingHeaders={pricingHeaders}
            customers={[contractCustomer]}
            onSubmit={handleContractSubmit}
            onCancel={() => setShowContractModal(false)}
          />
        </Modal>
      )}

      {showContractCreation && contractCustomer && (
        <ContractCreation
          customer={contractCustomer}
          onClose={() => setShowContractCreation(false)}
        />
      )}
    </div>
  );
};

export default Customers;